import { useMemo } from "react";
import { mapCategoriesFlat, normalizeLabel, getFilteredColorOptions, getFilteredSizeOptions, getFilteredOccasionOptions, getFilteredDesignerOptions, getFilteredAvailabilityOptions, getFilteredSleevesOptions, getFilteredMaterialOptions, getFilteredStoneOptions, restoreOriginalLabels } from "@/helpers/plpHelper";
import { colorCode } from "@/utils/constants/colorCode";
import { generalConstants } from "@/utils/constants/general";

export function useFilterOptions(facets, selected, getCategoryLabel) {
  const FILTER_KEYS = generalConstants.FILTER_KEYS;
  const categories = useMemo(() => mapCategoriesFlat(facets), [facets]);
  const selectedLvl0 = selected[FILTER_KEYS.CATEGORY]?.[0];
  const selectedLvl1 = selected[FILTER_KEYS.SUBCATEGORY] || [];
  const lvl0Options = useMemo(
  () =>
    selectedLvl0
      ? categories.filter(
          c => c.level === 0 && normalizeLabel(c.key) === normalizeLabel(selectedLvl0)
        )
      : categories.filter(c => c.level === 0),
  [categories, selectedLvl0]
);
  
const lvl1Options = useMemo(
  () =>
      categories.filter(
        c =>
          c.level === 1 &&
          selectedLvl0 &&
          normalizeLabel(c.parentKey) === normalizeLabel(selectedLvl0)
      ),
    [categories, selectedLvl0]
  );
  
  const lvl2Options = useMemo(
    () =>
      categories.filter(
        c =>
          c.level === 2 &&
          selectedLvl1.some(
            sel => normalizeLabel(c.parentKey) === normalizeLabel(sel)
          )
      ),
    [categories, selectedLvl1]
  );

  const colorOptions = useMemo(
    () => getFilteredColorOptions(facets),
    [facets]
  );
  const sizeOptions = useMemo(
    () => getFilteredSizeOptions(facets),
    [facets]
  );
  const occasionOptions = useMemo(
    () => getFilteredOccasionOptions(facets),
    [facets]
  );
  const designerOptions = useMemo(() => {
    const options = getFilteredDesignerOptions(facets);
    const facetValues = facets?.find(f => f.key === FILTER_KEYS.DESIGNERS)?.values || [];
    return restoreOriginalLabels(options, facetValues, normalizeLabel);
    }, [facets]
  );
  const availabilityOptions = useMemo(
    () => getFilteredAvailabilityOptions(facets),
    [facets]
  );
  const sleevesOptions = useMemo(
    () => getFilteredSleevesOptions(facets),
    [facets]
  );
  const materialOptions = useMemo(() => {
    const options = getFilteredMaterialOptions(facets);
    const facetValues = facets?.find(f => f.key === FILTER_KEYS.MATERIAL)?.values || [];
    return restoreOriginalLabels(options, facetValues, normalizeLabel);
    }, [facets]
  );
  const stoneOptions = useMemo(
    () => getFilteredStoneOptions(facets),
    [facets]
  );

  // Selected category and subcategory display names for titles
  const selectedCategoryObj = lvl0Options.find(
    opt => normalizeLabel(opt.key) === normalizeLabel(selected[FILTER_KEYS.CATEGORY]?.[0])
  );
  const selectedCategoryTitle = selectedCategoryObj && getCategoryLabel
    ? getCategoryLabel(selectedCategoryObj)
    : "Subcategory";

const selectedSubcategoryObjs = lvl1Options.filter(
  opt => (selected[FILTER_KEYS.SUBCATEGORY] || []).some(
    sel => normalizeLabel(opt.key) === normalizeLabel(sel)
  )
);
const selectedSubcategoryTitle = selectedSubcategoryObjs.length && getCategoryLabel
  ? selectedSubcategoryObjs.map(getCategoryLabel).join(", ")
  : "Sub-Subcategory";

  return {
    lvl0Options,
    lvl1Options,
    lvl2Options,
    colorOptions,
    sizeOptions,
    occasionOptions,
    designerOptions,
    availabilityOptions,
    sleevesOptions,
    materialOptions,
    stoneOptions,
    selectedCategoryTitle,
    selectedSubcategoryTitle,
  };
}